import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { catchError, forkJoin, of, Subject, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { CalendarService } from '../../core/localization/calendar.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { WORKFLOW_TASK_OPERATIONS } from '../../shared/workflow-surfaces/workflow-task-operations';
import { WorkflowTaskHostComponent } from '../../shared/workflow-surfaces/workflow-task-host.component';
import { WorkflowBpmnDiagramComponent, WorkflowBpmnElementSelection } from './workflow-bpmn-diagram.component';
import {
  ProcessDefinitionDetail,
  ProcessInstanceDetail,
  ProcessInstanceExecution,
  ProcessTask,
  WorkflowApiService
} from './workflow-api.service';
import { bindWorkflowTaskLiveUpdates } from './workflow-live-updates';

interface ProcessHistoryItem {
  id?: string;
  eventType: string;
  title: string;
  description: string;
  occurredAtUtc: string;
  dataJson?: string;
  taskId?: string | null;
}

interface FriendlyFact {
  key: string;
  label: string;
  value: string;
}

interface FriendlyHistoryEntry {
  title: string;
  summary: string;
  occurredAtUtc: string;
  severity: 'success' | 'danger' | 'warn' | 'info' | 'secondary';
  taskId?: string;
}

interface GraphNode {
  bpmnElementId: string;
  name: string;
  type: string;
}

type StepKind = 'current' | 'completed' | 'upcoming';

@Component({
  standalone: true,
  selector: 'ain-workflow-instance-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    ButtonModule,
    TagModule,
    RouterLink,
    WorkflowBpmnDiagramComponent,
    WorkflowTaskHostComponent
  ],
  providers: [
    MessageService,
    { provide: WORKFLOW_TASK_OPERATIONS, useExisting: WorkflowApiService }
  ],
  template: `
    <ain-page
      [title]="pageTitle()"
      [description]="pageDescription()">
      <a page-actions routerLink="/workflow/instances">{{ t('common.back') }}</a>
      <ain-info-hint
        page-title-addon
        [titleKey]="'workflow.instances.runtime.hint.title'"
        [descriptionKey]="'workflow.instances.runtime.hint.description'"
        [bulletKeys]="[
          'workflow.instances.runtime.hint.bullets.work',
          'workflow.instances.runtime.hint.bullets.chart',
          'workflow.instances.runtime.hint.bullets.steps'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (loadError()) {
        <ain-state type="error" [title]="loadError() ?? t('common.loadFailed')" />
      } @else if (instance(); as inst) {
        <section class="summary-card">
          <div class="summary-meta">
            <p-tag [value]="statusLabel(inst.status)" [severity]="statusSeverity(inst.status)" />
            <div>
              <span class="label">{{ t('workflow.instances.runtime.currentStep') }}</span>
              <strong>{{ currentStepName() }}</strong>
            </div>
            @if (inst.startDateUtc) {
              <div>
                <span class="label">{{ t('workflow.instances.runtime.startedAt') }}</span>
                <span>{{ formatWhen(inst.startDateUtc) }}</span>
              </div>
            }
          </div>
          <div class="summary-actions">
            @if (definition(); as def) {
              <a pButton [routerLink]="['/workflow/definitions', def.id]" [label]="t('workflow.instances.runtime.viewDefinition')"></a>
            }
            @if (canCancel()) {
              <button pButton type="button" severity="danger" [label]="t('workflow.cancel')" (click)="cancel()"></button>
            }
          </div>
        </section>

        @if (currentWorkTask(); as task) {
          <section class="work-card">
            <header>
              <h3>{{ t('workflow.instances.runtime.currentWork') }}</h3>
              <p-tag [value]="t('workflow.instances.runtime.youAreHere')" severity="info" />
            </header>
            @for (item of [task]; track item.id) {
              <ain-workflow-task-host
                [taskId]="item.id"
                [embedded]="true"
                (taskCompleted)="reload(false)" />
            }
          </section>
        } @else if (waitingTask(); as waiting) {
          <section class="work-card waiting">
            <h3>{{ t('workflow.instances.runtime.currentWork') }}</h3>
            <p>{{ t('workflow.instances.runtime.waiting') }}: {{ waiting.name }}</p>
          </section>
        } @else if (isActive()) {
          <section class="work-card waiting">
            <h3>{{ t('workflow.instances.runtime.currentWork') }}</h3>
            <p>{{ t('workflow.instances.runtime.noWork') }}</p>
          </section>
        }

        <section class="step-card">
          <h3>{{ t('workflow.instances.runtime.stepDetails') }}</h3>
          @if (selectedStep(); as step) {
            <div class="step-head">
              <strong>{{ step.name }}</strong>
              <p-tag [value]="stepKindLabel(step.kind)" [severity]="stepKindSeverity(step.kind)" />
            </div>
            <p class="step-summary">{{ stepKindSummary(step.kind) }}</p>
            @if (step.startedAtUtc) {
              <p class="muted">{{ t('workflow.instances.runtime.startedAt') }}: {{ formatWhen(step.startedAtUtc) }}</p>
            }
            @if (step.task && step.kind !== 'current') {
              <a [routerLink]="['/workflow/tasks', step.task.id]">{{ t('workflow.instances.runtime.openTask') }}</a>
            }
          } @else {
            <p class="hint">{{ t('workflow.instances.runtime.selectStep') }}</p>
          }
        </section>

        <section class="path-card">
          <header class="path-head">
            <h3>{{ t('workflow.instances.runtime.path') }}</h3>
            <div class="legend">
              <span class="swatch current"></span>{{ t('workflow.instances.runtime.youAreHere') }}
              <span class="swatch completed"></span>{{ t('workflow.instances.runtime.completed') }}
              <span class="swatch upcoming"></span>{{ t('workflow.instances.runtime.upcoming') }}
            </div>
          </header>
          @if (bpmnXml()) {
            <ain-workflow-bpmn-diagram
              [xml]="bpmnXml()"
              [currentElementIds]="currentElementIds()"
              [completedElementIds]="completedElementIds()"
              [upcomingElementIds]="upcomingElementIds()"
              [selectedElementId]="selectedElementId()"
              (elementSelected)="onElementSelected($event)" />
          } @else {
            <ain-state type="empty" [title]="t('workflow.instances.runtime.diagramEmpty')" />
          }
        </section>

        @if (processFacts().length > 0) {
          <section class="vars-card">
            <header class="section-head">
              <h3>{{ t('workflow.instances.runtime.variables') }}</h3>
              <ain-info-hint
                [titleKey]="'workflow.instances.runtime.variables.hint.title'"
                [descriptionKey]="'workflow.instances.runtime.variables.hint.description'" />
            </header>
            <div class="facts">
              @for (fact of processFacts(); track fact.key) {
                <article class="fact">
                  <span class="fact-label">{{ fact.label }}</span>
                  <span class="fact-value">{{ fact.value }}</span>
                </article>
              }
            </div>
          </section>
        }

        <details class="history-panel">
          <summary>{{ t('workflow.instances.runtime.historyCollapsed') }}</summary>
          <ain-info-hint
            [titleKey]="'workflow.instances.runtime.history.hint.title'"
            [descriptionKey]="'workflow.instances.runtime.history.hint.description'"
            [bulletKeys]="['workflow.instances.runtime.history.hint.bullets.read']" />
          @if (friendlyHistory().length === 0) {
            <p class="hint">{{ t('workflow.instances.runtime.history.empty') }}</p>
          } @else {
            <div class="timeline">
              @for (entry of friendlyHistory(); track entry.occurredAtUtc + entry.title) {
                <article class="timeline-item" [attr.data-severity]="entry.severity">
                  <div class="timeline-head">
                    <p-tag [severity]="entry.severity" [value]="entry.title" />
                    <span class="timestamp">{{ formatWhen(entry.occurredAtUtc) }}</span>
                  </div>
                  @if (entry.summary) {
                    <p class="summary">{{ entry.summary }}</p>
                  }
                  @if (entry.taskId) {
                    <a [routerLink]="['/workflow/tasks', entry.taskId]">{{ t('workflow.instances.runtime.openTask') }}</a>
                  }
                </article>
              }
            </div>
          }
        </details>
      }
    </ain-page>
  `,
  styles: [`
    a { display: inline-block; margin-bottom: 12px; }
    .summary-card, .work-card, .step-card, .path-card, .vars-card {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 16px;
      background: #fff;
    }
    .summary-card { display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; align-items: center; }
    .summary-meta, .summary-actions { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
    .label { display: block; color: #64748b; font-size: 12px; }
    .work-card header, .path-head, .step-head, .section-head { display: flex; justify-content: space-between; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .work-card h3, .step-card h3, .path-card h3, .vars-card h3 { margin: 0; }
    .waiting p, .step-summary { margin: 8px 0 0; color: #475569; }
    .muted { margin: 8px 0 0; color: #64748b; font-size: 13px; }
    .facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
    .fact { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; background: #f8fafc; display: grid; gap: 4px; }
    .fact-label { color: #64748b; font-size: 12px; }
    .fact-value { color: #0f172a; font-weight: 600; word-break: break-word; }
    .legend { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; color: #475569; font-size: 12px; }
    .swatch { width: 12px; height: 12px; border-radius: 3px; display: inline-block; border: 2px solid currentColor; }
    .swatch.current { background: #ede9fe; color: #6d28d9; }
    .swatch.completed { background: #d1fae5; color: #047857; }
    .swatch.upcoming { background: transparent; color: #94a3b8; }
    .timeline { display: flex; flex-direction: column; gap: 10px; margin: 12px 0 0; }
    .timeline-item { border: 1px solid #e2e8f0; border-inline-start: 4px solid #94a3b8; border-radius: 8px; padding: 10px 12px; background: #f8fafc; }
    .timeline-item[data-severity='success'] { border-inline-start-color: #059669; }
    .timeline-item[data-severity='info'] { border-inline-start-color: #7c3aed; }
    .timeline-item[data-severity='danger'] { border-inline-start-color: #dc2626; }
    .timeline-head { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .timestamp, .hint { color: #64748b; font-size: 12px; }
    .summary { margin: 8px 0 0; color: #334155; }
    .history-panel { margin-top: 8px; }
    .history-panel summary { cursor: pointer; font-weight: 600; margin-bottom: 12px; }
  `]
})
export class WorkflowInstanceDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly calendar = inject(CalendarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);

  readonly instance = signal<ProcessInstanceDetail | null>(null);
  readonly definition = signal<ProcessDefinitionDetail | null>(null);
  readonly history = signal<ProcessHistoryItem[]>([]);
  readonly tasks = signal<ProcessTask[]>([]);
  readonly executions = signal<ProcessInstanceExecution[]>([]);
  readonly inbox = signal<ProcessTask[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly selectedElementId = signal<string | null>(null);
  readonly selectedElementMeta = signal<WorkflowBpmnElementSelection | null>(null);
  private readonly reloadRequests = new Subject<{ id: string; showLoading: boolean }>();

  private readonly technicalKeys = new Set([
    'tenantid',
    'processinstanceid',
    'processdefinitionid',
    'initiatoruserid',
    'actoruserid',
    'assigneduserid',
    'assigneeid',
    'traceid',
    'routekey',
    'flowid',
    'gatewayid',
    'operatorid',
    'executionid',
    'taskid',
    'idempotencykey',
    'dedupekey',
    'analyticsversion',
    'sourceelementid',
    'targetelementid',
    'bpmelementid',
    'bpmnelementid',
    'configurationjson',
    'inputdatajson',
    'outputdatajson',
    'formdatajson',
    'variablesjson',
    'errordetailsjson',
    'auditjson',
    'correlationid',
    'conversationid'
  ]);

  readonly bpmnXml = computed(() => this.definition()?.latestBpmnXml?.trim() ?? '');
  readonly graphNodes = computed(() => this.parseGraphNodes(this.definition()?.latestParsedGraphSnapshot));
  readonly currentElementIds = computed(() => {
    const fromExecutions = this.executions()
      .filter(item => item.status === 'Active' || item.status === 'Pending')
      .map(item => item.bpmnElementId);
    const fromOpenTasks = this.openTasks().map(item => item.bpmnElementId);
    const ids = [...fromExecutions, ...fromOpenTasks];
    const fallback = this.instance()?.currentElementId;
    if (fallback) {
      ids.push(fallback);
    }
    return [...new Set(ids.filter(Boolean))];
  });
  readonly completedElementIds = computed(() => [
    ...new Set(
      this.executions()
        .filter(item => item.status === 'Completed')
        .map(item => item.bpmnElementId)
        .filter(id => !this.currentElementIds().includes(id))
    )
  ]);
  readonly upcomingElementIds = computed(() => {
    const current = new Set(this.currentElementIds());
    const completed = new Set(this.completedElementIds());
    return this.graphNodes()
      .map(item => item.bpmnElementId)
      .filter(id => id && !current.has(id) && !completed.has(id));
  });
  readonly openTasks = computed(() =>
    this.tasks().filter(task => {
      const status = task.status.toLowerCase();
      return status !== 'completed' && status !== 'cancelled' && status !== '4' && status !== '5';
    }));
  readonly currentWorkTask = computed(() => {
    const open = this.openTasks();
    if (open.length === 0) {
      return null;
    }
    const currentIds = new Set(this.currentElementIds());
    const matching = open.find(task => currentIds.has(task.bpmnElementId));
    const candidate = matching ?? open[0];
    return this.inbox().some(item => item.id === candidate.id) ? candidate : null;
  });
  readonly waitingTask = computed(() => {
    const open = this.openTasks();
    if (open.length === 0) {
      return null;
    }
    return this.currentWorkTask() ? null : open[0];
  });
  readonly selectedStep = computed(() => {
    const elementId = this.selectedElementId();
    if (!elementId) {
      return null;
    }
    const node = this.graphNodes().find(item => item.bpmnElementId === elementId);
    const meta = this.selectedElementMeta();
    const execution = this.executions().find(item => item.bpmnElementId === elementId);
    const task = [...this.tasks()].reverse().find(item => item.bpmnElementId === elementId) ?? null;
    const kind = this.resolveStepKind(elementId);
    const type = node?.type || meta?.type || execution?.elementType || task?.operatorType || '';
    const name = this.friendlyStepName(elementId, node?.name || meta?.name || task?.name, type);
    return {
      id: elementId,
      name,
      typeLabel: this.elementTypeLabel(type),
      kind,
      startedAtUtc: execution?.startedAtUtc,
      task
    };
  });
  readonly pageTitle = computed(() => this.definition()?.name || this.t('workflow.instances.detail'));
  readonly pageDescription = computed(() => {
    const inst = this.instance();
    if (!inst) {
      return '';
    }
    const reference = inst.referenceNumber || inst.businessKey;
    if (reference && !this.looksTechnical(reference)) {
      return `${this.statusLabel(inst.status)} · ${reference}`;
    }
    return this.statusLabel(inst.status);
  });
  readonly processFacts = computed(() => this.toFriendlyFacts(this.parseDataJson(this.instance()?.variablesJson)));
  readonly friendlyHistory = computed(() =>
    this.history()
      .map(item => this.toFriendlyHistory(item))
      .sort((a, b) => a.occurredAtUtc.localeCompare(b.occurredAtUtc)));

  ngOnInit(): void {
    this.reloadRequests.pipe(
      switchMap(request => this.loadDesk(request.id)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.loading.set(false);
      if (!result) {
        this.loadError.set(this.t('common.loadFailed'));
        return;
      }

      this.applyDesk(result.id, result.instance, result.bundle);
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.reload(true, id);
      }
    });
    bindWorkflowTaskLiveUpdates(
      {
        liveUpdateService: this.liveUpdateService,
        contextService: this.contextService,
        destroyRef: this.destroyRef
      },
      showLoading => this.reload(showLoading === true),
      null
    );
  }

  reload(showLoading = true, id = this.instance()?.id ?? this.route.snapshot.paramMap.get('id')): void {
    if (!id) {
      return;
    }
    if (showLoading) {
      this.loading.set(true);
      this.loadError.set(null);
      this.selectedElementId.set(null);
      this.selectedElementMeta.set(null);
    }

    this.reloadRequests.next({ id, showLoading });
  }

  private loadDesk(id: string) {
    return this.api.getInstance(id).pipe(
      switchMap(instance =>
        forkJoin({
          definition: this.api.getDefinition(instance.processDefinitionId).pipe(catchError(() => of(null))),
          history: this.api.getInstanceHistory(id).pipe(catchError(() => of([]))),
          tasks: this.api.getInstanceTasks(id).pipe(catchError(() => of([] as ProcessTask[]))),
          executions: this.api.getInstanceExecutions(id).pipe(catchError(() => of([] as ProcessInstanceExecution[]))),
          inbox: this.api.getInbox().pipe(catchError(() => of([] as ProcessTask[])))
        }).pipe(
          map(bundle => ({ id, instance, bundle }))
        )
      ),
      catchError(() => of(null))
    );
  }

  private applyDesk(
    id: string,
    instance: ProcessInstanceDetail,
    bundle: {
      definition: ProcessDefinitionDetail | null;
      history: unknown[];
      tasks: ProcessTask[];
      executions: ProcessInstanceExecution[];
      inbox: ProcessTask[];
    }
  ): void {
    this.instance.set(instance);
    this.definition.set(bundle.definition);
    this.history.set((bundle.history as ProcessHistoryItem[]) ?? []);
    const inbox = bundle.inbox ?? [];
    this.inbox.set(inbox);
    const loadedTasks = bundle.tasks ?? [];
    this.tasks.set(
      loadedTasks.length > 0
        ? loadedTasks
        : inbox.filter(item => item.processInstanceId === id)
    );
    this.executions.set(bundle.executions ?? []);
    if (!this.selectedElementId()) {
      const current = this.currentElementIds()[0] ?? instance.currentElementId;
      if (current) {
        this.selectedElementId.set(current);
      }
    }
  }

  onElementSelected(selection: WorkflowBpmnElementSelection): void {
    this.selectedElementId.set(selection.id);
    this.selectedElementMeta.set(selection);
  }

  isActive(): boolean {
    const status = this.instance()?.status?.toLowerCase();
    return status === 'active' || status === '2';
  }

  canCancel(): boolean {
    const status = this.instance()?.status?.toLowerCase();
    return status === 'active' || status === 'suspended' || status === '2' || status === '1';
  }

  statusLabel(status: string): string {
    const key = `workflow.instances.runtime.status.${status}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    const normalized = status.toLowerCase();
    if (normalized === 'completed' || normalized === '3') return 'success';
    if (normalized === 'failed' || normalized === '5') return 'danger';
    if (normalized === 'cancelled' || normalized === '4') return 'warn';
    if (normalized === 'suspended') return 'secondary';
    return 'info';
  }

  currentStepName(): string {
    const currentId = this.currentElementIds()[0];
    if (!currentId) {
      return '—';
    }
    const node = this.graphNodes().find(item => item.bpmnElementId === currentId);
    const task = this.openTasks().find(item => item.bpmnElementId === currentId);
    return this.friendlyStepName(currentId, node?.name || task?.name, node?.type || task?.operatorType);
  }

  stepKindLabel(kind: StepKind): string {
    if (kind === 'current') return this.t('workflow.instances.runtime.youAreHere');
    if (kind === 'completed') return this.t('workflow.instances.runtime.completed');
    return this.t('workflow.instances.runtime.upcoming');
  }

  stepKindSummary(kind: StepKind): string {
    return this.t(`workflow.instances.runtime.stepSummary.${kind}`);
  }

  stepKindSeverity(kind: StepKind): 'success' | 'info' | 'secondary' {
    if (kind === 'current') return 'info';
    if (kind === 'completed') return 'success';
    return 'secondary';
  }

  formatWhen(value?: string): string {
    return value ? this.calendar.formatDate(value, { hour: '2-digit', minute: '2-digit' }) : '—';
  }

  cancel(): void {
    const id = this.instance()?.id;
    if (!id) return;
    this.api.cancelInstance(id).subscribe({
      next: inst => {
        this.instance.set(inst);
        this.messages.add({ severity: 'success', summary: this.t('workflow.cancelled') });
        this.reload(false, id);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private resolveStepKind(elementId: string): StepKind {
    if (this.currentElementIds().includes(elementId)) return 'current';
    if (this.completedElementIds().includes(elementId)) return 'completed';
    return 'upcoming';
  }

  private elementTypeLabel(type: string): string {
    const normalized = type.replace('bpmn:', '');
    const key = `workflow.instances.runtime.types.${normalized}`;
    const translated = this.t(key);
    return translated === key ? (normalized || type || '—') : translated;
  }

  private friendlyStepName(_id: string, name?: string, type?: string): string {
    const candidate = (name || '').trim();
    if (candidate && !this.looksTechnical(candidate)) {
      return candidate;
    }
    const typeLabel = this.elementTypeLabel(type || '');
    if (typeLabel && typeLabel !== '—') {
      return typeLabel;
    }
    return this.t('workflow.instances.runtime.unnamedStep');
  }

  private looksTechnical(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) {
      return true;
    }
    if (this.isGuid(trimmed)) {
      return true;
    }
    if (/^(startEvent|endEvent|task|flow|gateway)[_-]/i.test(trimmed)) {
      return true;
    }
    if (/^[A-Za-z]+_\d+$/.test(trimmed)) {
      return true;
    }
    if (trimmed.includes(':') && this.isGuid(trimmed.split(':').pop() ?? '')) {
      return true;
    }
    return false;
  }

  private isGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
  }

  private toFriendlyFacts(source: Record<string, unknown>, prefix = ''): FriendlyFact[] {
    const facts: FriendlyFact[] = [];
    for (const [rawKey, rawValue] of Object.entries(source)) {
      const key = `${prefix}${rawKey}`;
      if (this.technicalKeys.has(rawKey.toLowerCase()) || rawKey.toLowerCase().endsWith('json')) {
        continue;
      }
      if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        facts.push(...this.toFriendlyFacts(rawValue as Record<string, unknown>, `${key}.`));
        continue;
      }
      const value = this.friendlyValue(rawValue);
      if (!value) {
        continue;
      }
      facts.push({
        key,
        label: this.factLabel(rawKey),
        value
      });
    }
    return facts;
  }

  private factLabel(key: string): string {
    const lookup = `workflow.instances.runtime.variable.${key}`;
    const translated = this.t(lookup);
    if (translated !== lookup) {
      return translated;
    }
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();
  }

  private friendlyValue(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'boolean') {
      return value ? this.t('common.yes') : this.t('common.no');
    }
    if (typeof value === 'number') {
      return String(value);
    }
    if (Array.isArray(value)) {
      const parts = value
        .map(item => this.friendlyValue(item))
        .filter((item): item is string => !!item);
      return parts.length > 0 ? parts.join('، ') : null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || this.looksTechnical(trimmed) || trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return null;
      }
      if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
        return this.formatWhen(trimmed);
      }
      const channel = this.channelLabel(trimmed);
      if (channel) {
        return channel;
      }
      return trimmed;
    }
    return null;
  }

  private channelLabel(value: string): string | null {
    const key = `workflow.instances.runtime.channel.${value}`;
    const translated = this.t(key);
    return translated === key ? null : translated;
  }

  private toFriendlyHistory(item: ProcessHistoryItem): FriendlyHistoryEntry {
    const details = this.parseDataJson(item.dataJson);
    const eventType = item.eventType ?? '';
    const title = this.historyTitle(eventType, item.title);
    const summary = this.historySummary(details);
    const taskId = typeof details['taskId'] === 'string' && !this.looksTechnical(String(details['taskId']))
      ? String(details['taskId'])
      : (eventType === 'task.completed' || eventType === 'task.created') && typeof details['taskId'] === 'string'
        ? String(details['taskId'])
        : undefined;

    return {
      title,
      summary,
      occurredAtUtc: item.occurredAtUtc,
      severity: this.resolveSeverity(eventType),
      taskId: eventType === 'task.completed' ? taskId : undefined
    };
  }

  private historyTitle(eventType: string, rawTitle?: string): string {
    const eventKey = `workflow.instances.runtime.event.${eventType.replace(/\./g, '_')}`;
    const eventLabel = this.t(eventKey);
    const resolvedEvent = eventLabel === eventKey ? this.resolveStatusLabel(eventType) : eventLabel;
    const title = (rawTitle || '').trim();
    if (
      (eventType.startsWith('task.') || eventType.startsWith('operator.')) &&
      title &&
      !this.looksTechnical(title) &&
      title.toLowerCase() !== resolvedEvent.toLowerCase()
    ) {
      return `${resolvedEvent} «${title}»`;
    }
    return resolvedEvent;
  }

  private historySummary(details: Record<string, unknown>): string {
    const parts: string[] = [];
    const channel = typeof details['ingressChannel'] === 'string' ? this.channelLabel(details['ingressChannel']) : null;
    if (channel) {
      parts.push(channel);
    }
    const reason = typeof details['reason'] === 'string' ? this.friendlyValue(details['reason']) : null;
    if (reason) {
      parts.push(reason);
    }
    return parts.join(' · ');
  }

  private parseGraphNodes(snapshot?: string): GraphNode[] {
    if (!snapshot?.trim()) {
      return [];
    }
    try {
      const parsed = JSON.parse(snapshot) as {
        operators?: Array<{ name?: string; type?: string; bpmnElementId?: string }>;
        gateways?: Array<{ name?: string; type?: string; bpmnElementId?: string }>;
        startTriggers?: Array<{ name?: string; type?: string }>;
      };
      const nodes: GraphNode[] = [];
      for (const item of parsed.operators ?? []) {
        if (item.bpmnElementId) {
          nodes.push({ bpmnElementId: item.bpmnElementId, name: item.name ?? item.bpmnElementId, type: item.type ?? '' });
        }
      }
      for (const item of parsed.gateways ?? []) {
        if (item.bpmnElementId) {
          nodes.push({ bpmnElementId: item.bpmnElementId, name: item.name ?? item.bpmnElementId, type: item.type ?? '' });
        }
      }
      return nodes;
    } catch {
      return [];
    }
  }

  private parseDataJson(raw?: string): Record<string, unknown> {
    if (!raw?.trim()) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }

  private resolveStatusLabel(eventType: string): string {
    const key = `workflow.instance.execution.status.${eventType.replace(/\./g, '_')}`;
    const translated = this.t(key);
    return translated === key ? eventType : translated;
  }

  private resolveSeverity(eventType: string): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    if (eventType.includes('failed')) return 'danger';
    if (eventType.includes('succeeded') || eventType.includes('completed') || eventType.includes('started')) return 'success';
    if (eventType.includes('retry')) return 'warn';
    if (eventType.includes('executing')) return 'info';
    return 'secondary';
  }
}
